/**
 * ADR-0013 — Supplier Balance Owner (Purchases / Payables).
 *
 * Read-SSOT: supplierDebts[].remainingDebt (+ remainingAmount Legacy)
 * sowie offene supermarketInvoices (Restbetrag).
 *
 * Bilan AP, Dashboard KPI und Payment-UI teilen dieselbe Payable-Regel:
 * Rest = totalAmount − paidAmount; CON-Parent zählt; aktive CON-Kinder nicht.
 * Soft-voided invoices (achat void) contribute 0 remaining (ADR-0016).
 */

import {
  isConsolidatedInvoiceRecord,
  refMatchesInvoice
} from '../../purchases/consolidatedInvoiceRefs';
import { isSupplierInvoiceVoided } from '../../purchases/purchaseVoidStatus';

/**
 * @param {object} [invoice]
 * @returns {number}
 */
export function resolveInvoiceRemainingAmount(invoice) {
  if (!invoice) return 0;
  if (isSupplierInvoiceVoided(invoice)) return 0;
  const total = Number(invoice.totalAmount ?? invoice.total ?? 0) || 0;
  const paid = Number(invoice.paidAmount) || 0;
  return Math.max(0, total - paid);
}

/**
 * @param {Array} [supplierDebts]
 * @param {string|number} supplierId
 * @returns {number}
 */
export function resolveSupplierBalance(supplierDebts = [], supplierId) {
  if (supplierId == null || supplierId === '') return 0;
  const debt = (supplierDebts || []).find(
    (d) => d && String(d.supplierId) === String(supplierId)
  );
  if (!debt) return 0;
  const remaining =
    Number(debt.remainingDebt) ||
    Number(debt.remainingAmount) ||
    0;
  return Math.max(0, remaining);
}

/**
 * Portfolio Verbindlichkeiten aus Debt-Records.
 * @param {Array} [supplierDebts]
 * @returns {number}
 */
export function summarizeAccountsPayable(supplierDebts = []) {
  if (!Array.isArray(supplierDebts)) return 0;
  return supplierDebts.reduce((sum, debt) => {
    const remaining =
      Number(debt?.remainingDebt) ||
      Number(debt?.remainingAmount) ||
      0;
    return sum + Math.max(0, remaining);
  }, 0);
}

function isConsolidatedInvoice(invoice) {
  return isConsolidatedInvoiceRecord(invoice);
}

/**
 * Kind-Rechnung einer noch aktiven konsolidierten Facture — Rest läuft über Parent.
 * Match wie PaymentService (id / purchaseId), nicht nur ref.id === invoice.id.
 */
export function isChildOfActiveConsolidated(invoice, invoices = []) {
  if (!invoice || isConsolidatedInvoice(invoice)) return false;
  if (isSupplierInvoiceVoided(invoice)) return false;
  if (!invoice.id && !invoice.purchaseId) return false;
  return (invoices || []).some((candidate) => {
    if (!isConsolidatedInvoice(candidate)) return false;
    if (isSupplierInvoiceVoided(candidate)) return false;
    const refs = candidate.originalInvoices || [];
    if (!refs.some((ref) => refMatchesInvoice(ref, invoice))) return false;
    return (
      resolveInvoiceRemainingAmount(candidate) > 0 ||
      (Number(candidate.paidAmount) || 0) > 0 ||
      (Array.isArray(candidate.payments) && candidate.payments.length > 0)
    );
  });
}

/**
 * Offene zahlbare Lieferanten-Rechnungen (Payment-UI / Dashboard / Bilan AP):
 * Rest = total − paid; CON-Parent inkl.; keine Doppelzählung der Kinder.
 * @param {Array} [supermarketInvoices]
 * @param {{ getRemaining?: Function }} [options]
 * @returns {{ count: number, totalRemaining: number, invoices: Array }}
 */
export function summarizeOpenPayableInvoices(
  supermarketInvoices = [],
  { getRemaining = resolveInvoiceRemainingAmount } = {}
) {
  const list = Array.isArray(supermarketInvoices) ? supermarketInvoices : [];
  const invoices = list.filter((invoice) => {
    if (isSupplierInvoiceVoided(invoice)) return false;
    if (Math.max(0, Number(getRemaining(invoice)) || 0) <= 0) return false;
    if (isChildOfActiveConsolidated(invoice, list)) return false;
    return true;
  });
  const totalRemaining = invoices.reduce(
    (sum, invoice) => sum + Math.max(0, Number(getRemaining(invoice)) || 0),
    0
  );
  return {
    count: invoices.length,
    totalRemaining,
    invoices
  };
}

/**
 * Offene Lieferanten-Rechnungen — Bilanz-AP (identisch zu summarizeOpenPayableInvoices).
 * @param {Array} [supermarketInvoices]
 * @param {{ getRemaining?: Function }} [options]
 * @returns {number}
 */
export function summarizeUnpaidInvoicesDebt(
  supermarketInvoices = [],
  { getRemaining = resolveInvoiceRemainingAmount } = {}
) {
  return summarizeOpenPayableInvoices(supermarketInvoices, { getRemaining })
    .totalRemaining;
}

/**
 * Bilanz Accounts Payable.
 * Active ledger = factures (supermarketInvoices). Legacy supplierDebts only when
 * there are no invoices — avoids double-counting the same purchase debts.
 */
export function summarizeTotalAccountsPayable({
  supplierDebts = [],
  supermarketInvoices = [],
  getInvoiceRemaining
} = {}) {
  const invoiceDebt = summarizeUnpaidInvoicesDebt(supermarketInvoices, {
    getRemaining: getInvoiceRemaining || resolveInvoiceRemainingAmount
  });
  const hasInvoiceLedger =
    Array.isArray(supermarketInvoices) && supermarketInvoices.length > 0;
  if (hasInvoiceLedger) {
    return invoiceDebt;
  }
  return summarizeAccountsPayable(supplierDebts) + invoiceDebt;
}

/**
 * Recalc Debt-Record aus Invoice-Zeilen (voided invoices excluded).
 */
export function computeSupplierDebtTotals({ invoices = [] } = {}) {
  const active = (invoices || []).filter((inv) => !isSupplierInvoiceVoided(inv));
  const totalDebt = active.reduce(
    (sum, inv) => sum + (Number(inv?.totalAmount) || 0),
    0
  );
  const paidAmount = active.reduce(
    (sum, inv) => sum + (Number(inv?.paidAmount) || 0),
    0
  );
  return {
    totalDebt,
    paidAmount,
    remainingDebt: totalDebt - paidAmount
  };
}
